import * as log from './log';

export const messageSendErrorId = error => {
  const codes = [error?.code, ...(error?.apiErrors || error?.data?.errors || []).map(e => e.code)];
  if (codes.includes('PHONE_VERIFICATION_REQUIRED')) return 'SendMessageForm.verifyPhone';
  if (codes.includes('new-user-message-blocked')) return 'SendMessageForm.newAccount';
  if (codes.includes('message-pending-review')) return 'SendMessageForm.pendingReview';
  if (codes.includes('message-send-skipped')) return 'SendMessageForm.notDelivered';
  if (codes.includes('message-send-unconfirmed') || codes.includes('message-send-timeout')) {
    return 'SendMessageForm.unconfirmed';
  }
  if (error?.status === 401) return 'SendMessageForm.sessionExpired';
  if ([403, 404].includes(error?.status)) return 'SendMessageForm.unavailable';
  if ([400, 422].includes(error?.status)) return 'SendMessageForm.invalidMessage';
  if (error?.status === 409) return 'SendMessageForm.conflict';
  if (error?.status === 429) return 'SendMessageForm.rateLimited';
  // A transport failure can occur after the message has been persisted.
  if (!error?.status) return 'SendMessageForm.unconfirmed';
  return 'SendMessageForm.sendFailed';
};

export const confirmedMessageId = response => {
  const message = response?.data?.data;
  if (
    response?.moderationStatus === 'pendingReview' ||
    message?.attributes?.status === 'pendingReview'
  ) {
    throw Object.assign(new Error('Message awaiting review'), { code: 'message-pending-review' });
  }
  if (message?.id && !message.attributes?.skipped) return message.id;
  throw Object.assign(new Error('Message delivery was not confirmed'), {
    code: message?.attributes?.skipped ? 'message-send-skipped' : 'message-send-unconfirmed',
  });
};

export const submitMessageDraft = async ({ values, form, send, onAttachmentSent, onSent }) => {
  const sendOne = async content => {
    const id = await send(content);
    if (!id)
      throw Object.assign(new Error('Missing message id'), { code: 'message-send-unconfirmed' });
    onSent?.(id);
  };
  for (const attachment of values.attachmentsUrls || []) {
    if (!attachment?.link) continue;
    await sendOne(attachment.link);
    onAttachmentSent?.(attachment);
  }
  const text = values.message?.trim();
  if (text) {
    await sendOne(text);
    form.change('message', '');
  }
};

// The send response confirms persistence. Render it even if a subsequent query
// fails; older servers return only an id, so retain the acknowledged content.
export const confirmedMessage = (response, content, sender) => {
  const id = confirmedMessageId(response);
  return {
    ...response.data.data,
    id,
    type: 'message',
    attributes: { content, createdAt: new Date(), ...response.data.data.attributes },
    sender,
  };
};

export const reportMessageSendFailure = (error, transactionId, source) => {
  // HTTP failures are reported by the server. Only report transport/confirmation
  // failures here, without passing the raw error (which can contain request data).
  if (error?.messageFailureReported || (error?.status && error?.requestId)) return;
  log.error(new Error('Message delivery could not be confirmed'), 'message-send-failed', {
    stage: ['message-send-skipped', 'message-send-unconfirmed', 'message-pending-review'].includes(
      error?.code
    )
      ? 'confirmation'
      : 'request',
    errorType: error?.name || 'Error',
    status: error?.status || null,
    transactionId: transactionId?.uuid || transactionId,
    source,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  });
};
